import { useEffect, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import axiosInstance from '../../helpers/axios';
import { downloadFile } from '../FibeCommissionBilling/utils/downloadFile';

const PREVIEW_COLUMNS = ['loan_id', 'borrower_name', 'amount', 'tenure', 'emi', 'disbursement_date', 'institute', 'fund_id'];

// Keeps the popup from rendering above the modal when there's no room below.
const AUTOCOMPLETE_POPPER_PROPS = {
    placement: 'bottom-start',
    modifiers: [{ name: 'flip', enabled: false }],
};

// Explicit (rather than relying on MUI's default) so matching institutes always narrow
// down as soon as the search text changes, regardless of list size.
const filterInstituteOptions = createFilterOptions({
    stringify: (option) => option.brandName || '',
});

export default function ExportFreeLoansModal({ open, onClose }) {
    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(null);
    const [amountMin, setAmountMin] = useState('');
    const [amountMax, setAmountMax] = useState('');
    const [institutes, setInstitutes] = useState([]);
    const [selectedInstituteId, setSelectedInstituteId] = useState('');

    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState(null);

    useEffect(() => {
        if (!open) return;
        setDateFrom(null);
        setDateTo(null);
        setAmountMin('');
        setAmountMax('');
        setSelectedInstituteId('');
        setDownloadError(null);

        axiosInstance
            .get('/institute/info', { params: { perPage: '200', pageNo: '1' } })
            .then((response) => setInstitutes(response.data.data.institutes))
            .catch(() => setInstitutes([]));
    }, [open]);

    const handleDownload = () => {
        setIsDownloading(true);
        setDownloadError(null);

        const params = new URLSearchParams();
        if (dateFrom) params.set('dateFrom', dateFrom.format('YYYY-MM-DD'));
        if (dateTo) params.set('dateTo', dateTo.format('YYYY-MM-DD'));
        if (selectedInstituteId) params.set('instituteId', selectedInstituteId);
        if (amountMin) params.set('amountMin', amountMin);
        if (amountMax) params.set('amountMax', amountMax);

        const today = new Date().toISOString().slice(0, 10);
        downloadFile(`/admin-settings/loan-hypothecation/export-free?${params.toString()}`, `free-loans-${today}.xlsx`)
            .catch(() => setDownloadError('Could not export free loans.'))
            .finally(() => setIsDownloading(false));
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
                <DialogTitle>📥 Export Free Loans (Unhypothecated)</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ marginBottom: '20px' }}>
                        Download an Excel file with all free loans. Fill in the "fund_id" column and re-upload to bulk assign.
                    </Typography>

                    {downloadError && <Alert severity="error" sx={{ marginBottom: '16px' }}>{downloadError}</Alert>}

                    <Box sx={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
                        <DatePicker
                            label="Disbursement From"
                            value={dateFrom}
                            onChange={setDateFrom}
                            slotProps={{ textField: { size: 'small', fullWidth: true } }}
                        />
                        <DatePicker
                            label="Disbursement To"
                            value={dateTo}
                            onChange={setDateTo}
                            slotProps={{ textField: { size: 'small', fullWidth: true } }}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
                        <TextField
                            size="small"
                            fullWidth
                            label="Min ₹"
                            type="number"
                            value={amountMin}
                            onChange={(e) => setAmountMin(e.target.value)}
                        />
                        <TextField
                            size="small"
                            fullWidth
                            label="Max ₹"
                            type="number"
                            value={amountMax}
                            onChange={(e) => setAmountMax(e.target.value)}
                        />
                    </Box>

                    <Autocomplete
                        size="small"
                        options={institutes}
                        getOptionLabel={(option) => option.brandName || ''}
                        filterOptions={filterInstituteOptions}
                        value={institutes.find((i) => i.id === selectedInstituteId) || null}
                        onChange={(e, value) => setSelectedInstituteId(value?.id || '')}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={(params) => (
                            <TextField {...params} label="Institute" placeholder="All Institutes" />
                        )}
                        ListboxProps={{ style: { maxHeight: 250 } }}
                        slotProps={{ popper: AUTOCOMPLETE_POPPER_PROPS }}
                        sx={{ marginBottom: '20px' }}
                    />

                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>
                        Excel Column Preview
                    </Typography>
                    <Table size="small" sx={{ border: '1px solid #e2e8f0' }}>
                        <TableHead>
                            <TableRow>
                                {PREVIEW_COLUMNS.map((col) => (
                                    <TableCell
                                        key={col}
                                        sx={col === 'fund_id' ? { backgroundColor: '#fef3c7', fontWeight: 600 } : { fontWeight: 600 }}
                                    >
                                        {col === 'fund_id' ? 'fund_id ← Fill this column' : col}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                    </Table>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={isDownloading}>Cancel</Button>
                    <Button variant="contained" onClick={handleDownload} disabled={isDownloading}>
                        {isDownloading ? 'Downloading...' : '📥 Download Excel'}
                    </Button>
                </DialogActions>
            </Dialog>
        </LocalizationProvider>
    );
}
